import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Phone, 
  ArrowRight, 
  Wrench, 
  Shield, 
  Clock, 
  MapPin,
  Star,
  Droplets,
  Disc,
  Cpu,
  Cog,
  Gauge,
  Thermometer,
  Battery,
  Settings
} from "lucide-react";

const HERO_BG = "https://customer-assets.emergentagent.com/job_trusted-car-service/artifacts/weczgace_IMG_0504.webp";
const PHONE_NUMBER = "(940) 337-9761";
const PHONE_LINK = "tel:+19403379761";

const services = [
  { icon: Droplets, name: "Oil Changes", description: "Keep your engine running smooth" },
  { icon: Disc, name: "Brake Repair", description: "Stop safely, every time" },
  { icon: Cpu, name: "Engine Diagnostics", description: "Find and fix any issue" },
  { icon: Cog, name: "Transmission", description: "Expert transmission service" },
  { icon: Gauge, name: "Tune-Ups", description: "Peak performance tuning" },
  { icon: Thermometer, name: "AC & Heating", description: "Stay comfortable year-round" },
];

const whyChooseUs = [
  { icon: Shield, title: "Honest Pricing", description: "No hidden fees, no surprises. Just fair, upfront pricing." },
  { icon: Wrench, title: "Experienced Mechanics", description: "Certified technicians with years of hands-on experience." },
  { icon: Clock, title: "Fast Turnaround", description: "Get back on the road quickly without sacrificing quality." },
  { icon: MapPin, title: "Local Trusted Shop", description: "Proudly serving Wichita Falls since day one." },
];

const testimonials = [
  {
    name: "Mike R.",
    text: "Tony's shop is the only place I trust with my truck. Fair prices and honest work every time.",
    rating: 5,
  },
  {
    name: "Sarah L.",
    text: "They diagnosed my engine problem in minutes when other shops couldn't figure it out. Highly recommend!",
    rating: 5,
  },
  {
    name: "James T.",
    text: "Great family-owned business. They treat you like a neighbor, not just a customer.",
    rating: 5,
  },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 hero-gradient" />
        
        <div className="container mx-auto px-4 md:px-8 lg:px-12 relative z-10 pt-24">
          <div className="max-w-2xl space-y-8 animate-fade-in-up">
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-foreground uppercase tracking-tight leading-tight">
              Honest Auto Repair{" "}
              <span className="text-primary">You Can Trust</span>
            </h1>
            
            <p className="text-lg md:text-xl text-secondary leading-relaxed">
              Fast, reliable, and affordable service for all makes and models.
              Serving Wichita Falls with quality auto care.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <a href={PHONE_LINK} data-testid="hero-call-btn">
                <Button className="cta-primary flex items-center gap-2 text-lg animate-pulse-glow">
                  <Phone className="w-5 h-5" />
                  Call Now {PHONE_NUMBER}
                </Button>
              </a>
              <Link to="/quote" data-testid="hero-quote-btn">
                <Button className="cta-secondary flex items-center gap-2 text-lg">
                  Request a Quote
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-secondary rounded-full flex justify-center">
            <div className="w-1 h-3 bg-primary rounded-full mt-2 animate-pulse" />
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section data-testid="services-preview" className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-foreground mb-4">
              Our <span className="text-primary">Services</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              From routine maintenance to major repairs, we've got you covered.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {services.map((service, index) => (
              <Card
                key={service.name}
                data-testid={`service-card-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="service-card bg-card border-border hover:border-primary/50 cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-6 md:p-8">
                  <service.icon className="service-icon mb-4" />
                  <h3 className="font-heading text-xl uppercase text-foreground mb-2">
                    {service.name}
                  </h3>
                  <p className="text-muted-foreground">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services" data-testid="view-all-services-btn">
              <Button className="cta-secondary flex items-center gap-2 mx-auto">
                View All Services
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section data-testid="why-choose-us" className="py-20 md:py-28 bg-card">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-foreground mb-4">
              Why Choose <span className="text-primary">Tony's</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              We're not just mechanics – we're your neighbors, committed to keeping 
              your vehicle safe and reliable.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div
                key={item.title}
                data-testid={`why-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-center space-y-4 animate-fade-in-up"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                  <item.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading text-xl uppercase text-foreground">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section data-testid="testimonials-section" className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-foreground mb-4">
              What Our <span className="text-primary">Customers Say</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.name}
                data-testid={`testimonial-${index}`}
                className="testimonial-card animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                  ))}
                </div>
                <p className="text-foreground mb-4 italic">"{testimonial.text}"</p>
                <p className="font-heading text-lg text-primary">— {testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section data-testid="cta-banner" className="py-16 md:py-24 bg-primary">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-white mb-4">
            Ready to Get Your Car Fixed?
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-xl mx-auto">
            Don't wait until it's too late. Call us today or request a free quote online.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={PHONE_LINK} data-testid="cta-call-btn">
              <Button className="bg-white text-primary hover:bg-gray-100 font-bold uppercase tracking-wider px-8 py-4 text-lg flex items-center gap-2">
                <Phone className="w-5 h-5" />
                {PHONE_NUMBER}
              </Button>
            </a>
            <Link to="/quote" data-testid="cta-quote-btn">
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary font-bold uppercase tracking-wider px-8 py-4 text-lg transition-all duration-300">
                Request a Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
